import type {PlateBlockNode, PlateInlineNode, PlateValue} from './resolver/types';

export type TermBlock = {heading: string; body: string};

const blockText = (block: PlateBlockNode): string =>
	(block.children as Array<PlateInlineNode | PlateBlockNode>)
		.map(child => {
			if ('text' in child && typeof child.text === 'string') return child.text;
			if ('children' in child) return blockText(child as PlateBlockNode);
			return '';
		})
		.join('');

const stripNumbering = (text: string): string => text.replace(/^\s*\d+[.)]\s*/, '').trim();

/**
 * Turns the terms rich text into numbered term blocks for the PDF. A heading block always
 * starts a new term; an empty paragraph also acts as a separator, after which the next
 * paragraph's first line becomes the heading. Remaining paragraphs join into the body.
 * Manual "1." / "2)" numbering is stripped — the PDF numbers the terms itself.
 */
export function parseTermsBlocks(value: PlateValue): TermBlock[] {
	const terms: TermBlock[] = [];
	let current: TermBlock | null = null;
	let separated = true;

	const push = () => {
		if (current && (current.heading || current.body)) {
			terms.push(current);
		}
		current = null;
	};

	for (const block of value) {
		const text = blockText(block).trim();
		if (text.length === 0) {
			push();
			separated = true;
			continue;
		}
		if (block.type === 'h1') {
			push();
			current = {heading: stripNumbering(text), body: ''};
			separated = false;
			continue;
		}
		if (separated || current == null) {
			current = {heading: stripNumbering(text), body: ''};
			separated = false;
			continue;
		}
		current.body = current.body ? `${current.body} ${text}` : text;
	}
	push();
	return terms;
}
